// Shared navigation registry — the single source of truth for what pages exist,
// how they're grouped, and who may see them. Used by the sidebar (Layout) and
// the global search palette (CommandPalette), so a new page added here becomes
// both navigable and searchable.
import {
  LayoutDashboard, FolderOpen, FileSignature,
  Truck, Users, Zap, Moon, BarChart2, ClipboardList, Clock, TrendingUp,
  CalendarDays, Package, PackageSearch, ShieldAlert, Fuel, ClipboardCheck, Route, FileText, FileUp,
  Activity, Camera, Layers, LogIn, Wrench, UserCog, Gavel, Gauge, Timer, BookUser, Car,
  ShieldCheck, Wallet, Boxes, Plane, GraduationCap, Building2, Receipt, Ban, DollarSign,
  Headphones, ScanLine, Flag, HardHat, CalendarRange, Smartphone, UserPlus, ListChecks, Shirt,
  Calculator, Sparkles, Signpost, Landmark, Trophy,
} from 'lucide-react';

// Dashboard sits on its own above the groups.
export const DASHBOARD_ITEM = { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' };

// Grouped, collapsible navigation. Each group is an accordion in the sidebar;
// the group holding the current route auto-expands. Order the items within a
// group so they read roughly in the order of the working day / workflow.
export const NAV_GROUPS = [
  {
    label: 'Shifts & Attendance', icon: ClipboardCheck, items: [
      { to: '/daily-tasks',             icon: ListChecks,     label: 'Daily Tasks', adminOnly: true },
      { to: '/daily-snapshot',          icon: Layers,         label: 'Daily Snapshot' },
      { to: '/shift-approvals',         icon: ClipboardCheck, label: 'Shift Approvals' },
      { to: '/login-check',             icon: LogIn,          label: 'Log-On Check' },
      { to: '/afternoon-check',         icon: Clock,          label: 'Afternoon Check' },
      { to: '/indoor-time',             icon: Timer,          label: 'Indoor Time' },
      { to: '/end-of-shift',            icon: ClipboardList,  label: 'Cut off period' },
      { to: '/hourly-drivers',          icon: Clock,          label: 'Hourly Drivers' },
      { to: '/driver-activity-summary', icon: Activity,       label: 'Driver Activity Summary' },
      { to: '/roster',                  icon: CalendarDays,   label: 'Leave Calendar' },
      { to: '/sorters-admin',           icon: UserCog,        label: 'Night Sorters' },
    ],
  },
  {
    label: 'Pay & Billing', icon: Wallet, items: [
      { to: '/rcti',           icon: Zap,         label: 'RCTI & Xero' },
      { to: '/gccd-report',    icon: BarChart2,   label: 'CTTCD Report' },
      { to: '/driver-review',  icon: Gauge,       label: 'Driver Review', adminOnly: true },
      { to: '/tax-invoices',   icon: FileText,    label: 'Tax Invoices' },
      { to: '/fuel-workbench', icon: Fuel,        label: 'Fuel Workbench' },
      { to: '/direct-entry',   icon: Package,     label: 'DoE & Depots' },
      { to: '/award-rates',    icon: ShieldAlert, label: 'Award Rate Engine' },
      { to: '/revenue',        icon: TrendingUp,  label: 'Revenue & Margin' },
      { to: '/licensing',      icon: DollarSign,  label: 'Licensing Calculator', adminOnly: true },
    ],
  },
  {
    label: 'Parcels & Routes', icon: Boxes, items: [
      { to: '/import-parcels',  icon: FileUp,        label: 'Import Parcels' },
      { to: '/parcel-audit',    icon: PackageSearch, label: 'Parcel Audit' },
      { to: '/scanner-audit',   icon: ScanLine,      label: 'Scanner Audit', adminOnly: true },
      { to: '/scanner-photos',  icon: Camera,        label: 'Scanner Photos' },
      { to: '/scanners',        icon: Smartphone,    label: 'Scanners', adminOnly: true },
      { to: '/driver-routes',   icon: Route,         label: 'Driver Routes' },
      { to: '/customer-cases',  icon: Headphones,    label: 'Customer Cases', adminOnly: true },
      { to: '/standing-orders', icon: Signpost,      label: 'Standing Orders', adminOnly: true },
      { to: '/driver-issues',   icon: Flag,          label: 'Issue Register', adminOnly: true },
      { to: '/depot-map',       icon: Building2,     label: 'Depot Floor Plan' },
      { to: '/ap-intelligence', icon: FileText,      label: 'AP Intelligence' },
      { to: '/wahr-comparison', icon: TrendingUp,    label: 'WAHR Comparison' },
      { to: '/wahr-import',     icon: FileUp,        label: 'Import WAHR' },
    ],
  },
  {
    label: 'Tenders & Pricing', icon: Moon, items: [
      { to: '/night-sort',    icon: Moon,          label: 'Night Sort Pricing' },
      { to: '/peak-planning', icon: CalendarRange, label: 'Peak Planning', adminOnly: true },
      { to: '/contract-reviews', icon: FileSignature, label: 'Contract Review Requests', adminOnly: true },
      { to: '/tender-model',  icon: Calculator,    label: 'Tender Cost Model', adminOnly: true },
    ],
  },
  {
    label: 'Vehicles', icon: Truck, items: [
      { to: '/fleet',            icon: Truck,       label: 'Fleet' },
      { to: '/vehicle-service',  icon: Wrench,      label: 'Service & Repairs' },
      { to: '/traffic-fines',    icon: Gavel,       label: 'Fines & Infringements', adminOnly: true },
      // Two views on the same accident while the claims register beds in: Driver Accidents
      // is the raw feed of what drivers file from the portal (and where their photos land);
      // Accident / Insurance Claims is the claim we build from it and send to Allianz.
      { to: '/driver-accidents', icon: Car,         label: 'Driver Accidents', adminOnly: true },
      { to: '/vehicle-claims',   icon: ShieldAlert, label: 'Accident / Insurance Claims', adminOnly: true },
    ],
  },
  {
    label: 'People', icon: Users, items: [
      { to: '/personnel',  icon: Users,         label: 'Personnel' },
      { to: '/onboarding', icon: UserPlus,      label: 'Onboarding', adminOnly: true },
      { to: '/drivers',    icon: BookUser,      label: 'Drivers' },
      { to: '/uniforms',   icon: Shirt,         label: 'Uniform Register', adminOnly: true },
      { to: '/night-shift', icon: Moon,         label: 'Night Shift' },
      { to: '/retraining', icon: GraduationCap, label: 'Retraining', adminOnly: true },
      { to: '/melbourne-cup', icon: Trophy,     label: 'Melbourne Cup Sweep', adminOnly: true },
    ],
  },
  {
    label: 'Compliance & Docs', icon: FolderOpen, items: [
      { to: '/safety',     icon: HardHat,       label: 'Safety', adminOnly: true },
      // A tab of Safety, listed separately because it is what people go looking for by
      // name — and because being here is what makes it searchable from the dashboard.
      { to: '/pre-card',   icon: ShieldAlert,   label: 'Pre-Card Points', adminOnly: true },
      // Also a tab of Safety: the incident report we send Australia Post.
      { to: '/incident-reports', icon: ShieldAlert, label: 'Incident Reports', adminOnly: true },
      { to: '/documents',  icon: FolderOpen,    label: 'Documents' },
      { to: '/compliance', icon: ShieldCheck,   label: 'Compliance', adminOnly: true },
      { to: '/immigration', icon: Plane,        label: 'Immigration', adminOnly: true },
      { to: '/contracts',  icon: FileSignature, label: 'Contracts' },
      { to: '/receipts',   icon: Receipt,       label: 'Receipts', adminOnly: true },
      // The family's ATO payment plans (Paul, Holly, the businesses) — personal tax
      // records, pinned to Paul alone. The function gate (ownerLogin) is the real wall.
      { to: '/ato-plans',  icon: Landmark,      label: 'ATO Payment Plans', adminOnly: true, onlyLogins: ['BOUVENGP2'] },
      // Nails by Asha — a DIFFERENT business (Asha's own ABN) that lives in the engine as
      // a tenant. Pinned to Paul alone, and opened in a new tab because it is a separate
      // app with no sidebar: following it in place would strand him in a portal with no
      // way back but the browser's Back button.
      { to: '/nails', icon: Sparkles, label: 'Nails by Asha', adminOnly: true, onlyLogins: ['BOUVENGP2'], newTab: true },
      { to: '/mail-stoppers', icon: Ban,        label: 'Mail Stoppers', adminOnly: true },
      { to: '/mso',        icon: ShieldAlert,   label: 'MSO Tracker' },
    ],
  },
];

// Pages visible to team leaders (viewer role)
export const VIEWER_PAGES = new Set([
  '/dashboard', '/daily-snapshot', '/driver-activity-summary',
  '/roster', '/driver-routes', '/scanner-photos', '/afternoon-check', '/login-check',
]);

// Pages granted to an INDIVIDUAL team leader on top of VIEWER_PAGES. Billy Dong runs
// the Darlinghurst customer cases, so he gets the Customer Cases register (scoped to
// his own area by the backend) and the Scanner Audit he needs to investigate them —
// without opening either page to the other team leaders.
export const VIEWER_LOGIN_PAGES = {
  '/customer-cases': ['DONGB3'],
  '/scanner-audit': ['DONGB3'],
  // All three team leaders get the Safety page, scoped by the backend to the drivers in
  // their own area. Under the WHS plan the team leader is the one who investigates an
  // unsafe delivery point, so a safety report has to reach them — the plan, the topic
  // library and the focus stay with the office. KEEP IN SYNC with SAFETY_LEADERS in
  // netlify/functions/_shared/team-leaders.js.
  '/safety': ['DONGB3', 'SOTOASTOL2', 'SOTOCRUZL'],
  '/pre-card': ['DONGB3', 'SOTOASTOL2', 'SOTOCRUZL'],
};
const grantedTo = (to, login) => (VIEWER_LOGIN_PAGES[to] || []).includes(login);

// Role filter shared by the sidebar and the palette — keep the two surfaces
// showing exactly the same set of pages for a given user.
//
// `onlyLogins` pins an entry to named people and outranks every role rule, including
// admin. It exists for Nails by Asha: that is a different business, and an engine admin
// being able to SEE it in the sidebar would be untidy even though the function gate
// already refuses them. Hiding is not the security boundary — the gate is — but the
// sidebar should not advertise a door that only one person may open.
export const visibleNavItems = (items, { isViewer, isAdmin, login }) =>
  items.filter(({ to, adminOnly, onlyLogins }) => {
    if (onlyLogins && !onlyLogins.includes(login)) return false;
    return isViewer ? (VIEWER_PAGES.has(to) || grantedTo(to, login)) : (!adminOnly || isAdmin);
  });
